import { CommonModule } from "@angular/common";
import { Component, computed, inject } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { rescheduleMeta } from "@env/environment.commons";
import { OrderActionBase } from "@features/order-actions/models/order-action.model";
import {
  AvailableDatesPayload,
  OrderReschedulePayload,
} from "@features/order-actions/models/order-reschedule.model";
import { OrderRescheduleState } from "@features/order-actions/services/order-reschedule.state";
import { HorusLoadingComponent } from "@shared/components/domain/horus-loading/horus-loading.component";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { getAriaTicketUrl } from "app/core/utils/url";
import { HorusMessageGeneralComponent } from "../../../../shared/components/domain/horus-message-general/horus-message-general.component";
import { AvailableDatePipe } from "../../../../shared/pipes/available-date.pipe";
import { ConfirmDialogComponent } from "./confirm-dialog/confirm-dialog.component";
import { HorusBtnDirective } from "@shared/directives/horus-btn.directive";

@Component({
  selector: "app-order-reschedule-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatButtonToggleModule,
    HorusMessageGeneralComponent,
    HorusLoadingComponent,
    AvailableDatePipe,
    HorusBtnDirective,
  ],
  templateUrl: "./order-reschedule-dialog.component.html",
  styleUrl: "./order-reschedule-dialog.component.scss",
})
export class OrderRescheduleDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<OrderRescheduleDialogComponent>,
  );
  private readonly dialog = inject(MatDialog);
  readonly state = inject(OrderRescheduleState);
  private readonly fb = inject(FormBuilder);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);

  readonly data = inject<OrderActionBase>(MAT_DIALOG_DATA);
  readonly rescheduleOrderForm: FormGroup;

  readonly searchValue = computed(() => this.tenantUrlUtil.documentValue());
  readonly tenantId = computed(() => this.tenantUrlUtil.currentTenant());
  readonly daterange = computed(() => this.tenantUrlUtil.dateRange());
  readonly agent = computed(() => this.tenantUrlUtil.agent());

  constructor() {
    this.rescheduleOrderForm = this.fb.group({
      rescheduleDate: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.data.tenantId && this.data.orderId) {
      const payload: AvailableDatesPayload = {
        tenantId: this.data.tenantId,
        orderId: this.data.orderId,
      };
      this.state.availableDatesPayload.set(payload);
    }
  }

  private convertToISOFormat(dateStr: string): string {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  }

  rescheduleOrder(): void {
    if (
      !this.data.tenantId ||
      !this.data.orderId ||
      !this.rescheduleOrderForm.valid
    ) {
      return;
    }

    const dateValue = this.rescheduleOrderForm.value.rescheduleDate;
    const deliveryDate = this.convertToISOFormat(dateValue);

    const payload: OrderReschedulePayload = {
      tenantId: this.data.tenantId,
      orderId: this.data.orderId,
      deliveryDate,
      user: this.agent(),
      ...rescheduleMeta,
    };

    this.state.resetSubmitState();
    this.dialog.open(ConfirmDialogComponent, {
      height: "342px",
      width: "427px",
      data: { ...payload, orderType: this.data.orderType },
      disableClose: false,
      restoreFocus: false,
      panelClass: "confirm-dialog-panel",
    });
  }

  getAriaUrl(): string {
    const currentTenant = this.tenantId();
    if (!currentTenant) {
      return "";
    }
    return getAriaTicketUrl(
      currentTenant,
      this.data.orderId!,
      this.data.orderType!,
    );
  }

  redirectToAria(): void {
    const url = this.getAriaUrl();
    if (url) {
      window.open(url, "_blank");
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
